// src/components/WeatherOnDay.ts
import "./WeatherOnDay.css";
import { renderAIRichText } from "./AIRichText";

const AI_OVERVIEW_API_URL = "https://api.ayesearch.com/api/ai/overview/";

type AnyRecord = Record<string, any>;

function escapeHtml(value: unknown): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function finiteNumber(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function overviewText(data: AnyRecord): string {
  const overview = data?.overview;

  if (typeof overview === "string") return overview;

  if (overview && typeof overview === "object") {
    return String(overview.overview || overview.summary || "");
  }

  return String(data?.answer || "");
}

function historyData(data: AnyRecord): AnyRecord | null {
  const candidates = [
    data?.weather_history,
    data?.overview?.weather_history,
    data?.data?.weather_history,
    data?.result?.weather_history,
  ];

  for (const candidate of candidates) {
    if (candidate && typeof candidate === "object" && !Array.isArray(candidate)) {
      return candidate as AnyRecord;
    }
  }

  return null;
}

function formatRange(low: unknown, high: unknown, suffix: string): string {
  const a = finiteNumber(low);
  const b = finiteNumber(high);

  if (a === null && b === null) return "—";
  if (a === null) return `${Math.round(b!)}${suffix}`;
  if (b === null) return `${Math.round(a)}${suffix}`;
  return `${Math.round(a)}${suffix} – ${Math.round(b)}${suffix}`;
}

function formatCoverage(first: unknown, last: unknown): string {
  const parse = (value: unknown): string => {
    const raw = String(value || "").trim();
    if (!raw) return "";

    const match = raw.match(/(\d{1,2}):(\d{2})$/);
    if (!match) return raw;

    let hour = Number(match[1]);
    const minute = match[2];
    const suffix = hour >= 12 ? "PM" : "AM";
    hour %= 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute} ${suffix}`;
  };

  const start = parse(first);
  const end = parse(last);

  if (start && end) return start === end ? start : `${start} – ${end}`;
  return start || end || "—";
}

function renderStructuredHistory(history: AnyRecord): string {
  const count = Math.max(0, Number(history.observation_count || 0));
  const condition = String(history.dominant_condition || "").trim();
  const dateLabel = String(history.date_label || history.date || "Historical weather").trim();
  const location = String(history.location || "Requested location").trim();
  const peakWind = finiteNumber(history.peak_wind_mph);

  if (!count) {
    return `
      <section class="aye-weather-on-day" aria-label="Historical weather">
        <div class="aye-weather-on-day-kicker">AYESEARCH WEATHER HISTORY</div>
        <h2>${escapeHtml(location)}</h2>
        <div class="aye-weather-on-day-date">${escapeHtml(dateLabel)}</div>
        <p class="aye-weather-on-day-empty">
          AyeSearch does not have a retained observation for this location on that date.
        </p>
        <div class="aye-weather-on-day-source">
          AyeSearch retained weather cache
        </div>
      </section>
    `;
  }

  return `
    <section class="aye-weather-on-day" aria-label="Historical weather">
      <div class="aye-weather-on-day-kicker">AYESEARCH WEATHER HISTORY</div>

      <div class="aye-weather-on-day-header">
        <div>
          <h2>${escapeHtml(location)}</h2>
          <div class="aye-weather-on-day-date">${escapeHtml(dateLabel)}</div>
        </div>
        <div class="aye-weather-on-day-count">
          ${count} retained observation${count === 1 ? "" : "s"}
        </div>
      </div>

      <div class="aye-weather-on-day-condition">
        ${escapeHtml(condition || "Recorded conditions")}
      </div>

      <div class="aye-weather-on-day-grid">
        <div>
          <span>Temperature</span>
          <strong>${escapeHtml(formatRange(history.temp_f_low, history.temp_f_high, "°F"))}</strong>
        </div>
        <div>
          <span>Humidity</span>
          <strong>${escapeHtml(formatRange(history.humidity_low, history.humidity_high, "%"))}</strong>
        </div>
        <div>
          <span>Peak wind</span>
          <strong>${peakWind === null ? "—" : `${escapeHtml(peakWind.toFixed(1).replace(/\.0$/, ""))} mph`}</strong>
        </div>
        <div>
          <span>Coverage</span>
          <strong>${escapeHtml(formatCoverage(history.first_observed_at, history.last_observed_at))}</strong>
        </div>
      </div>

      <div class="aye-weather-on-day-source">
        Historical observations retained by AyeSearch · no live forecast used
      </div>
    </section>
  `;
}

export async function renderWeatherOnDay(
  container: HTMLElement,
  query: string,
): Promise<void> {
  container.innerHTML = `
    <section class="aye-weather-on-day aye-weather-on-day-loading">
      <div></div>
      <div></div>
      <div></div>
    </section>
  `;

  try {
    const url = new URL(AI_OVERVIEW_API_URL);
    url.searchParams.set("q", query);

    const response = await fetch(url.toString(), {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json" },
    });

    if (!response.ok) {
      throw new Error(`Historical weather returned HTTP ${response.status}`);
    }

    const data = (await response.json()) as AnyRecord;
    const history = historyData(data);

    if (history) {
      container.innerHTML = renderStructuredHistory(history);
      return;
    }

    // Compatibility fallback for deployments where the overview endpoint
    // exposes only the deterministic answer text. This still never calls the
    // current forecast endpoint or uses device location.
    const text = overviewText(data);
    if (!text) throw new Error("Historical weather response was empty.");

    container.innerHTML = `
      <section class="aye-weather-on-day" aria-label="Historical weather">
        <div class="aye-weather-on-day-kicker">AYESEARCH WEATHER HISTORY</div>
        <div class="aye-weather-on-day-answer">
          ${renderAIRichText(text, { variant: "overview" })}
        </div>
        <div class="aye-weather-on-day-source">
          Historical observations retained by AyeSearch · no live forecast used
        </div>
      </section>
    `;
  } catch (error) {
    console.error("AyeSearch historical weather card failed:", error);
    container.innerHTML = `
      <section class="aye-weather-on-day">
        <div class="aye-weather-on-day-kicker">AYESEARCH WEATHER HISTORY</div>
        <p class="aye-weather-on-day-empty">
          Historical weather is temporarily unavailable.
        </p>
      </section>
    `;
  }
}
